import React, { useState, useEffect, useCallback } from 'react';
import api, { accountsAPI, creditorsAPI, businessesAPI } from '../services/api';
import { InstitutionFields, useBankNames } from '../features/accounts/InstitutionFields';
import { formatApiError } from '../utils/errorMessage';
import { utilization, usedLabel } from '../utils/balanceSign';
import RecalculateBalanceButton from '../features/accounts/RecalculateBalanceButton';
import StartingBalanceHistory from '../features/accounts/StartingBalanceHistory';
import LoanTermsFields from '../features/accounts/LoanTermsFields';
import StartingBalanceFields, { startingBalanceChange, saveStartingBalance, confirmStartingBalance } from '../features/accounts/StartingBalanceFields';
import ErrorBoundary from '../components/ErrorBoundary';
import { BankLinkSetup } from '../features/bank-feeds';
import { BalanceWithSource, UnverifiedTotal, isBalanceLocked } from '../features/accounts/BalanceSource'; import UnassignedAccounts from '../features/accounts/UnassignedAccounts'; import { accountTypeOptions, isLiabilityType } from '../features/accounts/accountTypes';
import { accountFormChanged, NO_CHANGES } from '../features/accounts/accountFormChanges';

const Accounts = ({ selectedBusiness }) => {
  const [accounts, setAccounts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [addMode, setAddMode] = useState('choose'); // Add account: choose (bank or manual) | manual
  const showManualForm = useCallback(() => setAddMode('manual'), []);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formType, setFormType] = useState('checking');
  const [isLiability, setIsLiability] = useState(false);
  const [errors, setErrors] = useState({});
  const [showHelp, setShowHelp] = useState(false);
  const [apiError, setApiError] = useState('');
  const [formNotice, setFormNotice] = useState('');
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'datasheet'
  const [liabilitiesMinimized, setLiabilitiesMinimized] = useState(false);
  const [assetsMinimized, setAssetsMinimized] = useState(false);
  const [creditors, setCreditors] = useState([]);
  const [selectedCreditorId, setSelectedCreditorId] = useState('');
  const [businesses, setBusinesses] = useState([]);
  const [formBusinessId, setFormBusinessId] = useState('');

  useEffect(() => {
    businessesAPI.getAll()
      .then((res) => setBusinesses(Array.isArray(res.data) ? res.data : []))
      .catch(() => setBusinesses([]));
  }, []);

  useEffect(() => {
    loadAccounts();
    loadCreditors();
  }, [selectedBusiness]);

  const bankNames = useBankNames(selectedBusiness);
  const loadCreditors = async () => {
    try {
      // Only fetch creditors for the selected business + global creditors
      const params = { is_active: 'true' };
      if (selectedBusiness) {
        params.business_id = selectedBusiness;
      }
      const response = await creditorsAPI.getAll(params);
      setCreditors(response.data || []);
    } catch (error) {
      console.error('Failed to load creditors:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
    }
  };

  useEffect(() => {
    if (editingAccount) {
      setFormType(editingAccount.account_type || 'checking');
      setIsLiability(editingAccount.is_liability === undefined || editingAccount.is_liability === null ? isLiabilityType(editingAccount.account_type) : !!editingAccount.is_liability);
      setSelectedCreditorId(editingAccount.creditor_id || '');
      setFormBusinessId(editingAccount.business_id ? String(editingAccount.business_id) : '');
    } else {
      setFormBusinessId(selectedBusiness ? String(selectedBusiness) : '');
      setFormType('checking');
      setIsLiability(false);
      setSelectedCreditorId('');
    }
  }, [editingAccount]);

  const loadAccounts = async () => {
    try {
      const params = selectedBusiness ? { business_id: selectedBusiness } : {};
      const response = await accountsAPI.getAll(params);
      setAccounts(response.data);
    } catch (error) {
      console.error('Failed to load accounts:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData);
    // An unchecked checkbox is left out of FormData; send Active explicitly so unchecking it saves.
    const activeBox = e.target.elements?.is_active; if (activeBox) data.is_active = !!activeBox.checked; data.is_liability = isLiability;
    const nextErrors = {};
    const opening = startingBalanceChange(data, editingAccount);
    if (opening?.error) nextErrors.opening_balance = opening.error;
    if (!data.account_name || data.account_name.trim() === '') {
      nextErrors.account_name = 'Account name is required.';
    }
    if (!data.account_type) {
      nextErrors.account_type = 'Account type is required.';
    }
    // Credit limit is optional (closed or historical cards often have none).
    if (data.credit_limit !== undefined && data.credit_limit !== '' && !(parseFloat(data.credit_limit) >= 0)) {
      nextErrors.credit_limit = 'Credit limit must be a positive number, or leave it blank.';
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setFormNotice('');
    if (editingAccount && !opening && !accountFormChanged(data, editingAccount)) {
      setFormNotice(NO_CHANGES);
      return;
    }
    try {
      setApiError('');
      let accountId = editingAccount?.id;
      if (editingAccount) {
        await accountsAPI.update(editingAccount.id, data);
      } else {
        accountId = (await accountsAPI.create(data))?.data?.id;
      }
      if (opening && accountId) {
        // Show what the new starting balance does to the current balance before saving it.
        const ok = await confirmStartingBalance(api, accountId, opening, formatCurrency);
        if (ok) await saveStartingBalance(api, accountId, opening);
      }
      setShowModal(false);
      setEditingAccount(null);
      loadAccounts();
    } catch (error) {
      console.error('Failed to save account:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      setApiError(formatApiError(error, 'Failed to save account'));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Close this account? It will be marked inactive.')) {
      try {
        await accountsAPI.delete(id);
        loadAccounts();
      } catch (error) {
        console.error('Failed to delete account:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const normalizeUrl = (url) => {
    if (!url) return '';
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
  };

  const getCreditorLabel = (account) => account.creditor_name || account.institution || '—';

  const getCreditorLink = (account, variant = 'inline') => {
    if (!account.creditor_url) return null;

    const href = normalizeUrl(account.creditor_url);
    if (variant === 'button') {
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-1 items-center justify-center gap-1 bg-blue-50 text-blue-600 px-3 py-1.5 rounded text-sm font-medium hover:bg-blue-100 transition"
        >
          <span>Visit site</span>
          <span aria-hidden>↗</span>
        </a>
      );
    }

    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline"
      >
        <span>Visit site</span>
        <span aria-hidden>↗</span>
      </a>
    );
  };

  const assetTotal = accounts[0]?.asset_total || 0;
  const liabilityTotal = accounts[0]?.liability_total || 0;
  const totalBalance = accounts[0]?.net_balance || 0;

  // Group and sort accounts
  const liabilityAccounts = accounts
    .filter(acc => acc.is_liability)
    .sort((a, b) => (a.institution || '').localeCompare(b.institution || ''));
  const assetAccounts = accounts
    .filter(acc => !acc.is_liability)
    .sort((a, b) => (a.institution || '').localeCompare(b.institution || ''));

  return (
    <ErrorBoundary section="Accounts">
      <div className="space-y-6"><UnverifiedTotal accounts={accounts} format={formatCurrency} /><UnassignedAccounts businesses={businesses} refreshKey={accounts} format={formatCurrency} onChanged={loadAccounts} onEdit={(a) => { setEditingAccount(a); setShowModal(true); }} />
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Accounts</h2>
          <p className="text-gray-600 mt-1">Manage your bank accounts and credit cards</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setViewMode(viewMode === 'cards' ? 'datasheet' : 'cards')}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm font-medium"
            title={viewMode === 'cards' ? 'Switch to datasheet view' : 'Switch to card view'}
          >
            {viewMode === 'cards' ? '📊 Datasheet View' : '🃏 Card View'}
          </button>
          <button
            onClick={() => { setAddMode('choose'); setShowModal(true); setEditingAccount(null); }}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            + Add Account
          </button>
        </div>
      </div>

      {/* Total Balance Card (Assets − Liabilities) */}
      <div className={`rounded-lg shadow p-4 ${
        totalBalance < 0 
          ? 'bg-gradient-to-r from-purple-600 to-purple-800 text-black' 
          : 'bg-gradient-to-r from-blue-600 to-blue-800 text-white'
      }`}>
        <p className="text-sm opacity-90">Total Balance</p>
        <p className="text-3xl font-bold mt-1">{formatCurrency(totalBalance)}</p>
        <p className="mt-1 text-xs opacity-80">Assets {formatCurrency(assetTotal)} − Liabilities {formatCurrency(liabilityTotal)}</p>
      </div>

      {/* Liabilities Section */}
      {liabilityAccounts.length > 0 && (
        <div>
          <div className="mb-3">
            <h3 id="liability-accounts" className="text-lg font-bold text-red-700 flex items-center">
              <span className="mr-2">💳</span> Liabilities ({liabilityAccounts.length})
            </h3>
          </div>
          {/* Liabilities Total Banner */}
          <div className="bg-gradient-to-r from-purple-600 to-purple-800 text-white rounded-lg shadow p-3 mb-3 flex justify-between items-center">
            <div>
              <p className="text-xs opacity-90">Total Liabilities</p>
              <p className="text-2xl font-bold mt-0.5">{formatCurrency(liabilityTotal)}</p>
            </div>
            <button
              onClick={() => setLiabilitiesMinimized(!liabilitiesMinimized)}
              className="text-white hover:text-gray-200 text-2xl font-medium w-10 h-10 flex items-center justify-center"
              title={liabilitiesMinimized ? 'Expand' : 'Minimize'}
            >
              {liabilitiesMinimized ? '+' : '−'}
            </button>
          </div>

          {!liabilitiesMinimized && (
            <>
          {viewMode === 'datasheet' ? (
            /* Datasheet View for Liabilities */
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 table-fixed">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-48">Account Name</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-32">Institution</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Type</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-28">Balance</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-28">Credit Limit</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-20">Used</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Account #</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-20">Status</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-28">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {liabilityAccounts.map((account) => {
                      const visitLink = getCreditorLink(account);
                      return (
                        <tr key={account.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm font-medium text-gray-900" style={{ maxWidth: '200px', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                            {account.account_name}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600" title={getCreditorLabel(account)}>
                            <div className="flex flex-col">
                              <span className="truncate max-w-[12rem]">{getCreditorLabel(account)}</span>
                              {visitLink && (
                                <span className="mt-0.5 text-xs">{visitLink}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600 capitalize">
                            {account.account_type?.replace('_', ' ')}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm font-semibold text-red-600">
                            <BalanceWithSource account={account} format={formatCurrency} />
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">
                            {account.credit_limit ? formatCurrency(account.credit_limit) : '—'}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">
                            {usedLabel(account)}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">
                            {account.account_number_last4 ? `•••• ${account.account_number_last4}` : '—'}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded ${
                              account.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {account.is_active ? 'Active' : 'Closed'}
                            </span>
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm">
                            <button
                              onClick={() => { setEditingAccount(account); setShowModal(true); }}
                              className="text-blue-600 hover:text-blue-800 mr-3"
                            >
                              Edit
                            </button>
                            <RecalculateBalanceButton account={account} format={formatCurrency} onDone={loadAccounts} />
<StartingBalanceHistory account={account} format={formatCurrency} />
                            <button
                              onClick={() => handleDelete(account.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            /* Card View for Liabilities */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {liabilityAccounts.map((account) => {
                const visitInline = getCreditorLink(account);
                const visitButton = getCreditorLink(account, 'button');
                return (
                  <div key={account.id} className="bg-white rounded-lg shadow overflow-hidden">
                    <div className={`h-1 ${
                      account.account_type === 'checking' ? 'bg-blue-500' :
                      account.account_type === 'savings' ? 'bg-green-500' :
                      account.account_type === 'credit_card' ? 'bg-purple-500' :
                      'bg-gray-500'
                    }`} />
                    <div className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="text-base font-bold text-gray-900">{account.account_name}</h3>
                          <div className="text-xs text-gray-600">
                            <p>{getCreditorLabel(account)}</p>
                            {visitInline && (
                              <span className="mt-0.5 inline-block">{visitInline}</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 capitalize">{account.account_type?.replace('_', ' ')}</p>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          account.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {account.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    <div className="mb-3">
                      <p className="text-xs text-gray-600">Current Balance</p>
                      <p className={`text-2xl font-bold ${
                        account.is_liability ? 'text-red-600' : (account.current_balance >= 0 ? 'text-green-600' : 'text-red-600')
                      }`}>
                        <BalanceWithSource account={account} format={formatCurrency} />
                      </p>
                    </div>
                    {account.is_liability && Number(account.credit_limit) > 0 && account.is_active !== false && (
                      <div className="mb-3 p-2 bg-gray-50 rounded">
                        <p className="text-xs text-gray-600">Credit Limit</p>
                        <p className="text-sm font-semibold">{formatCurrency(account.credit_limit)}</p>
                        <div className="mt-1">
                          <div className="bg-gray-200 rounded-full h-1.5">
                            <div
                              className="bg-purple-600 h-1.5 rounded-full"
                              style={{ width: `${Math.min(utilization(account) * 100, 100)}%` }}
                            />
                          </div>
                          <p className="text-xs text-gray-600 mt-0.5">
                            {usedLabel(account) === 'Over limit' ? 'Over limit' : `${usedLabel(account)} used`}
                          </p>
                        </div>
                      </div>
                    )}
                    {account.account_number_last4 && (
                      <p className="text-xs text-gray-600 mb-3">
                        •••• {account.account_number_last4}
                      </p>
                    )}
                    
                      <div className="flex flex-wrap gap-2">
                        {visitButton}
                        <button
                          onClick={() => { setEditingAccount(account); setShowModal(true); }}
                          className="flex-1 bg-blue-50 text-blue-600 px-3 py-1.5 rounded text-sm hover:bg-blue-100 transition"
                        >
                          Edit
                        </button>
                        <RecalculateBalanceButton account={account} format={formatCurrency} onDone={loadAccounts} />
<StartingBalanceHistory account={account} format={formatCurrency} />
                        <button
                          onClick={() => handleDelete(account.id)}
                          className="flex-1 bg-red-50 text-red-600 px-3 py-1.5 rounded text-sm hover:bg-red-100 transition"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          </>
          )}
        </div>
      )}

      {/* Assets Section */}
      {assetAccounts.length > 0 && (
        <div>
          <div className="mb-3">
            <h3 id="asset-accounts" className="text-lg font-bold text-green-700 flex items-center">
              <span className="mr-2">💰</span> Assets ({assetAccounts.length})
            </h3>
          </div>
          
          {/* Assets Total Banner */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg shadow p-3 mb-3 flex justify-between items-center">
            <div>
              <p className="text-xs opacity-90">Total Assets</p>
              <p className="text-2xl font-bold mt-0.5">{formatCurrency(assetTotal)}</p>
            </div>
            <button
              onClick={() => setAssetsMinimized(!assetsMinimized)}
              className="text-white hover:text-gray-200 text-2xl font-medium w-10 h-10 flex items-center justify-center"
              title={assetsMinimized ? 'Expand' : 'Minimize'}
            >
              {assetsMinimized ? '+' : '−'}
            </button>
          </div>

          {!assetsMinimized && (
            <>
          {viewMode === 'datasheet' ? (
            /* Datasheet View for Assets */
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 table-fixed">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-48">Account Name</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-32">Institution</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Type</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-28">Balance</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-28">Credit Limit</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-20">Used</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Account #</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-20">Status</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-28">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {assetAccounts.map((account) => {
                      const visitLink = getCreditorLink(account);
                      return (
                        <tr key={account.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm font-medium text-gray-900" style={{ maxWidth: '200px', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                            {account.account_name}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600" title={getCreditorLabel(account)}>
                            <div className="flex flex-col">
                              <span className="truncate max-w-[12rem]">{getCreditorLabel(account)}</span>
                              {visitLink && (
                                <span className="mt-0.5 text-xs">{visitLink}</span>
                              )}
                            </div>
                          </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600 capitalize">
                          {account.account_type?.replace('_', ' ')}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm font-semibold text-green-600">
                          <BalanceWithSource account={account} format={formatCurrency} />
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">
                          —
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">
                          —
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">
                          {account.account_number_last4 ? `•••• ${account.account_number_last4}` : '—'}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded ${
                            account.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {account.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => { setEditingAccount(account); setShowModal(true); }}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                Edit
                              </button>
                              <RecalculateBalanceButton account={account} format={formatCurrency} onDone={loadAccounts} />
<StartingBalanceHistory account={account} format={formatCurrency} />
                              <button
                                onClick={() => handleDelete(account.id)}
                                className="text-red-600 hover:text-red-800"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            /* Card View for Assets */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {assetAccounts.map((account) => {
                const visitInline = getCreditorLink(account);
                const visitButton = getCreditorLink(account, 'button');
                return (
                  <div key={account.id} className="bg-white rounded-lg shadow overflow-hidden">
                    <div className={`h-1 ${
                      account.account_type === 'checking' ? 'bg-blue-500' :
                      account.account_type === 'savings' ? 'bg-green-500' :
                      account.account_type === 'credit_card' ? 'bg-purple-500' :
                      'bg-gray-500'
                    }`} />
                    <div className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="text-base font-bold text-gray-900">{account.account_name}</h3>
                          <div className="text-xs text-gray-600">
                            <p>{getCreditorLabel(account)}</p>
                            {visitInline && (
                              <span className="mt-0.5 inline-block">{visitInline}</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 capitalize">{account.account_type?.replace('_', ' ')}</p>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          account.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {account.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    
                    <div className="mb-3">
                      <p className="text-xs text-gray-600">Current Balance</p>
                      <p className={`text-2xl font-bold ${
                        account.is_liability ? 'text-red-600' : (account.current_balance >= 0 ? 'text-green-600' : 'text-red-600')
                      }`}>
                        <BalanceWithSource account={account} format={formatCurrency} />
                      </p>
                    </div>
                    
                    {account.account_number_last4 && (
                      <p className="text-xs text-gray-600 mb-3">
                        •••• {account.account_number_last4}
                      </p>
                    )}
                    
                      <div className="flex flex-wrap gap-2">
                        {visitButton}
                        <button
                          onClick={() => { setEditingAccount(account); setShowModal(true); }}
                          className="flex-1 bg-blue-50 text-blue-600 px-3 py-1.5 rounded text-sm hover:bg-blue-100 transition"
                        >
                          Edit
                        </button>
                        <RecalculateBalanceButton account={account} format={formatCurrency} onDone={loadAccounts} />
<StartingBalanceHistory account={account} format={formatCurrency} />
                        <button
                          onClick={() => handleDelete(account.id)}
                          className="flex-1 bg-red-50 text-red-600 px-3 py-1.5 rounded text-sm hover:bg-red-100 transition"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
            </>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">{editingAccount ? 'Edit' : 'Add'} Account</h3>
            {!editingAccount && addMode === 'choose' ? (
              <BankLinkSetup selectedBusiness={selectedBusiness} accounts={accounts} onManual={showManualForm}
                onCancel={() => setShowModal(false)} onDone={() => { setShowModal(false); loadAccounts(); }} />
            ) : (<>
            <button
              type="button"
              onClick={() => setShowHelp(!showHelp)}
              className="mb-3 text-sm text-blue-600 hover:underline"
            >
              {showHelp ? 'Hide help' : 'Need help?'}
            </button>
            {showHelp && (
              <div className="mb-4 p-3 rounded bg-gray-50 border border-gray-200 text-sm text-gray-700">
                <p className="mb-1">- Pick Account Type. Credit cards/LOC/loans are liabilities.</p>
                <p className="mb-1">- Classification defaults from type; you may override.</p>
                <p className="mb-1">- Credit Limit is required for liabilities; hidden for assets.</p>
              </div>
            )}
            {formNotice && <div className="mb-3 p-3 rounded bg-blue-50 border border-blue-200 text-blue-800" role="status">{formNotice}</div>}
            {apiError && (
              <div className="mb-3 p-3 rounded bg-red-50 border border-red-200 text-red-700 flex items-start justify-between" role="alert">
                <span>{formatApiError(apiError, 'Failed to save account')}</span>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await api.post('/support', {
                        area: 'accounts',
                        message: apiError,
                        url: window.location.href,
                        path: window.location.pathname,
                        payload: { formType, isLiability, selectedBusiness }
                      });
                      alert('Thanks! Your report was sent.');
                    } catch (e) {
                      alert('Could not send report.');
                    }
                  }}
                  className="ml-3 text-sm text-blue-700 hover:underline"
                >Report issue</button>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Business / Profile</label>
                <select
                  name="business_id"
                  value={formBusinessId}
                  onChange={(e) => setFormBusinessId(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">Personal (no business)</option>
                  {businesses.map((b) => (
                    <option key={b.id} value={String(b.id)}>{b.name}</option>
                  ))}
                </select>
                {editingAccount && String(editingAccount.business_id || '') !== formBusinessId && (
                  <p className="text-xs text-amber-700 mt-1">
                    Saving moves this account and its transactions, subscriptions and recurring bills to the selected profile. Its creditor is matched or copied there automatically.
                  </p>
                )}
              </div>
              <input type="hidden" name="is_liability" value={isLiability ? 'true' : 'false'} />
              
              <div>
                <label className="block text-sm font-medium mb-1">Account Name</label>
                <input
                  type="text"
                  name="account_name"
                  defaultValue={editingAccount?.account_name || ''}
                  required
                  className="w-full border rounded px-3 py-2"
                />
                {errors.account_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.account_name}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Account Type</label>
                <select
                  name="account_type"
                  value={formType}
                  onChange={(e) => {
                    const next = e.target.value;
                    setFormType(next);
                    const nextLower = next.toLowerCase();
                    if (isLiabilityType(nextLower)) {
                      setIsLiability(true);
                    } else {
                      setIsLiability(false);
                    }
                  }}
                  required
                  className="w-full border rounded px-3 py-2"
                >
                  {accountTypeOptions(editingAccount?.account_type).map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                {errors.account_type && (
                  <p className="mt-1 text-sm text-red-600">{errors.account_type}</p>
                )}
              </div>
              {['loan', 'mortgage'].includes(formType) && <LoanTermsFields account={editingAccount} />}
              <StartingBalanceFields account={editingAccount} error={errors.opening_balance} />

              <div>
                <label className="block text-sm font-medium mb-1">Classification</label>
                <select
                  value={isLiability ? 'liability' : 'asset'}
                  onChange={(e) => setIsLiability(e.target.value === 'liability')}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="asset">Asset</option>
                  <option value="liability">Liability</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Defaults from type; you can override if needed.</p>
              </div>
              
              <div>
                <InstitutionFields
                  institution={editingAccount?.institution || ''}
                  creditors={creditors}
                  bankNames={bankNames}
                  creditorId={selectedCreditorId}
                  onCreditorChange={setSelectedCreditorId}
                  error={errors.creditor_id}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Last 4 Digits</label>
                <input
                  type="text"
                  name="account_number_last4"
                  defaultValue={editingAccount?.account_number_last4 || ''}
                  maxLength="4"
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Current Balance</label>
                <input
                  type="number"
                  name="current_balance"
                  step="0.01"
                  defaultValue={editingAccount?.current_balance || '0'}
                  required readOnly={isBalanceLocked(editingAccount)} title={isBalanceLocked(editingAccount) ? 'Calculated from the starting balance or bank feed' : undefined}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              
              {isLiability && (
              <div>
                <label className="block text-sm font-medium mb-1">Credit Limit (optional; leave blank for closed or old cards)</label>
                <input
                  type="number"
                  name="credit_limit"
                  step="0.01"
                  defaultValue={editingAccount?.credit_limit || ''}
                  className="w-full border rounded px-3 py-2"
                />
                {errors.credit_limit && (
                  <p className="mt-1 text-sm text-red-600">{errors.credit_limit}</p>
                )}
              </div>
              )}
              {editingAccount && !isLiability && (
              <div>
                <label htmlFor="low_balance_threshold" className="block text-sm font-medium mb-1">Low balance alert</label>
                <input
                  id="low_balance_threshold"
                  type="number"
                  name="low_balance_threshold"
                  step="0.01"
                  placeholder="No alert"
                  defaultValue={editingAccount?.low_balance_threshold ?? ''}
                  className="w-full border rounded px-3 py-2"
                />
                <p className="mt-1 text-xs text-gray-500">Get an email or text when the balance drops below this amount. Leave blank for no alert.</p>
              </div>
              )}
              
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_active"
                    defaultChecked={editingAccount?.is_active !== false}
                    className="mr-2"
                  />
                  <span className="text-sm">Active</span>
                </label>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingAccount(null); }}
                  className="px-4 py-2 border rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={Object.keys(errors).length > 0}
                  className="px-4 py-2 bg-blue-600 disabled:bg-blue-300 text-white rounded hover:bg-blue-700"
                >
                  {editingAccount ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
            </>)}
          </div>
        </div>
      )}
      </div>
    </ErrorBoundary>
  );
};

export default Accounts;
